import { Card, Button} from "@/components/ui";
import { PhotoSlider, FormSelect, CountUp } from "@/components";
import useDashboard from "./useDashboard";
import {
  TriangleAlert,
  CircleCheck,
  HardHat,
  Settings,
  Clock4,
  Calendar,
  ChartColumn,
  FileCheck,
  User,
  Image,
  X,
  MousePointer2,
} from "lucide-react";
import {
  TITLE,
  VALUE,
  SUBTITLE,
  TYPE,
  NAME,
  PROGRESS,
  STATUS,
  PROJECT_MANAGERS,
  SITE_MANAGERS,
  SAFETY_OFFICERS,
  QC_OFFICERS,
  PROJECT_PLANS,
  REVISIONS,
  PROJECT_CLOSURE,
  CATEGORY,
  DELAYED,
  SAFETY,
  QUALITY,
  TOTAL_PHOTOS,
  PENDING_APPROVAL,
  APPROVED,
  REJECTED,
  PROJECT_LIST,
  bgMap,
  statusStyles,
} from "./consts";
import { images } from "@/assets";
import { iconMap } from "./iconMap";
import {
  getProjectStatusClassName,
  getProjectHealthLabel,
} from "@/lib/utils";

const Dashboard = () => {
  const {
    loggedInUserData,
    statsCards,
    safetyCompliance,
    qualityControl,
    adminQueue,
    workforceOverview,
    timeRange,
    projectHealthData,
    filteredActiveProjects,
    selectedHealthFilter,
    setSelectedHealthFilter,
    overallProgress,
    categoryPerformance,
    projectList,
    clientSharedPhotos,
    selectedProject,
    keys,
    requiredFields,
    siteManagerLastUpdate,
    form,
    isPhotoSliderOpen,
    setIsPhotoSliderOpen,
    selectedCategory,
    setSelectedCategory,
    selectedPhotos,
    safetyAuditSummary,
    qualityAuditSummary,
    handleMajorCountClick,
    handleReviewPhoto,
    upcomingEvents,
    scheduleVariance,
    closureReadiness,
    projectPlanVersion
  } = useDashboard();


  const { errors } = form.formState;


  return (
    <div className="p-4 lg:p-6 space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-lg text-gray-600">
          Hello, {loggedInUserData?.fullName || ""}
        </h1>
        <h2 className="text-2xl font-semibold">Admin</h2>
      </div>

      {/* Stats Cards */}
      <Card className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards?.map((card: any, index: number) => (
            <div
              key={index}
              className={`p-4 rounded-xl border flex justify-between items-start ${bgMap[card[TYPE]]}`}
            >
              <div>
                <p className="text-sm text-gray-600">{card[TITLE]}</p>
                <p className="text-2xl font-bold mt-1"><CountUp value={card[VALUE]} /></p>
                <p className="text-xs text-gray-500 mt-1">{card[SUBTITLE]}</p>
              </div>
              {iconMap[card[TYPE]]}
            </div>
          ))}
        </div>
      </Card>

      {/*   Quality */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-1">

            <HardHat className="w-6 h-6 text-yellow-500" />
            <h3 className="text-md font-semibold">Safety Compliance</h3>
          </div>

          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-gray-600">Major Non-Compliance</p>
            <span className="border text-xs px-2 py-1 bg-red-500 text-white rounded-full">
              {safetyCompliance?.majorNonCompliance}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Minor Non-Compliance</p>
            <span className="border text-xs px-2 py-1 rounded-full">
              {safetyCompliance?.minorNonCompliance}
            </span>
          </div>


          <p className="text-xs text-gray-400 mt-2">
            Tasks blocked due to safety violations
          </p>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Settings className="w-6 h-6 text-yellow-500" />
            <h3 className="text-md font-semibold">Quality Control</h3>
          </div>

          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-gray-600">Minor Failure</p>
            <span className="border text-xs px-2 py-1 rounded-full">
              {qualityControl?.minorFailure}
            </span>
          </div>

          <div className="flex justify-between items-center mb-1">
            <p className="text-sm text-gray-600">Major Failure</p>
            <span className="border text-xs px-2 py-1 bg-red-500 text-white rounded-full">
              {qualityControl?.majorFailure}
            </span>
          </div>


          <p className="text-xs text-gray-400 mt-2">
            QC issues preventing task closure
          </p>
        </Card>
      </div>
      {/* Project Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2 relative overflow-hidden">
          <div className="absolute bottom-4 right-4 pointer-events-none opacity-20 z-0">
            <img src={images.logo1} className="w-[180px]" alt="watermark" />
          </div>

          <div className="relative z-10">
            <div className="mb-4">
              <h3 className="text-md font-semibold">Project Health Overview</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <button
                type="button"
                onClick={() => setSelectedHealthFilter("on_track")}
                className={`rounded-xl border p-4 text-left transition-all ${selectedHealthFilter === "on_track"
                  ? "bg-green-100 border-green-500"
                  : "bg-white border-gray-200 hover:bg-green-100 hover:cursor-pointer"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md text-gray-500">On Track</p>
                    <p className="text-2xl font-semibold mt-1">
                      <CountUp value={projectHealthData[timeRange].on_track} />
                    </p>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-green-200 ${selectedHealthFilter === "on_track"
                      ? "bg-green-150"
                      : "bg-gray-150"
                      }`}
                  >
                    <CircleCheck className="text-green-600 w-5 h-5 bg-green-200" />
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedHealthFilter("delayed")}
                className={`rounded-xl border p-4 text-left transition-all ${selectedHealthFilter === "delayed"
                  ? "bg-yellow-50 border-yellow-400"
                  : "bg-white border-gray-200 hover:bg-primary-100 hover:cursor-pointer"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md text-gray-500">Delayed</p>
                    <p className="text-2xl font-semibold mt-1">
                      <CountUp value={projectHealthData[timeRange].delayed} />
                    </p>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-primary-100 ${selectedHealthFilter === "delayed"
                      ? "bg-yellow-100"
                      : "bg-gray-100"
                      }`}
                  >
                    <Clock4 className="text-yellow-600 w-5 h-5 " />
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedHealthFilter("critical")}
                className={`rounded-xl border p-4 text-left transition-all ${selectedHealthFilter === "critical"
                  ? "bg-red-50 border-red-400"
                  : "bg-white border-gray-200 hover:bg-red-100 hover:cursor-pointer"
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md text-gray-500">Critical</p>
                    <p className="text-2xl font-semibold mt-1">
                      <CountUp value={projectHealthData[timeRange].critical} />
                    </p>
                  </div>
                  <div
                    className={`p-3 rounded-xl bg-red-50 ${selectedHealthFilter === "critical"
                      ? "bg-red-100"
                      : "bg-gray-100"
                      }`}
                  >
                    <TriangleAlert className="w-5 h-5 text-red-500" />
                  </div>
                </div>
              </button>
            </div>

            <div>
              <h3 className="text-md font-semibold mb-3">Active Projects Snapshot</h3>

              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-white/80 [&::-webkit-scrollbar-thumb]:rounded-full transition-all">
                {filteredActiveProjects?.map((project: any, index: number) => {
                  const style =
                    statusStyles[project[STATUS]] || statusStyles.on_track;

                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between rounded-lg px-4 py-3 ${style.row}`}
                    >
                      <div>
                        <p className="font-medium text-gray-800">{project[NAME]}</p>
                        <p className="text-xs text-gray-500">
                          Progress: {project[PROGRESS]}%
                        </p>
                      </div>

                      <span
                        className={`min-w-[96px] text-center px-4 py-1.5 text-xs rounded-full font-medium capitalize ${style.badge}`}
                      >
                        {getProjectHealthLabel(project[STATUS])}
                      </span>
                    </div>
                  );
                })}

                {filteredActiveProjects?.length === 0 && (
                  <div className="rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-500 text-center">
                    No projects found for {selectedHealthFilter?.replace("_", " ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
        <div className="space-y-6">
          {/* Admin Queue */}
          <Card className="p-5">
            <h3 className="text-md font-semibold">Admin Action Queue</h3>
            <div className="space-y-4 mt-2">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Project plans awaiting approval</p>
                <span className="border text-xs px-2 py-1 bg-red-500 text-white rounded-full">
                  {adminQueue[PROJECT_PLANS]}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Impact schedule revisions</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {adminQueue[REVISIONS]}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Projects eligible for closure</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {adminQueue[PROJECT_CLOSURE]}
                </span>
              </div>
            </div>

            <div className="mt-6 mb-20">
              <Button className="w-full">Review All</Button>
            </div>
          </Card>

          {/* Workforce */}
          <Card className="p-5">
            <h3 className="text-md font-semibold">Workforce Overview</h3>

            <div className="space-y-4 mt-2">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Project Managers</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {workforceOverview[PROJECT_MANAGERS]}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Site Managers</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {workforceOverview[SITE_MANAGERS]}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">Safety Officers</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {workforceOverview[SAFETY_OFFICERS]}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600 mb-6">QC Officers</p>
                <span className="border text-xs px-2 py-1 rounded-full">
                  {workforceOverview[QC_OFFICERS]}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <Card className="p-6 bg-gray-50">
        <Card className="p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <MousePointer2 className="w-6 h-6 text-yellow-500 mt-1" />
              <div>
                <h3 className="text-md font-semibold mb-2">Select Project</h3>
                <p className="text-sm text-gray-500">
                  Full project execution visibility
                </p>
              </div>
            </div>

            <div className="w-full md:w-[280px]">
              <FormSelect
                key={keys[PROJECT_LIST]}
                name={PROJECT_LIST}
                control={form.control}
                label="Select Project"
                placeholder="Please select"
                error={errors?.project_list?.message}
                isRequired={requiredFields.includes(PROJECT_LIST)}
                options={(projectList?.data || []).map((project: any) => ({
                  label: project?.name,
                  value: String(project?.projectId),
                }))}
              />
            </div>
          </div>

        </Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-3">
          <div>
            <h3 className="text-md font-semibold">
              {selectedProject?.name || ""}
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              Status:{" "}
              <span
                className={getProjectStatusClassName(selectedProject?.status)}
              >
                {getProjectHealthLabel(selectedProject?.status)}
              </span>{" "}
              • Plan Version {projectPlanVersion || "N/A"} • Planned End: {scheduleVariance?.plannedEndDate ? new Date(scheduleVariance.plannedEndDate).toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
            </p>
          </div>
        </div>
        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-0">
            {overallProgress?.map((card: any, index: number) => (
              <div
                key={index}
                className={`p-4 rounded-xl border flex justify-between items-start ${bgMap[card[TYPE]]}`}>
                <div>
                  <p className="text-sm text-gray-600">{card[TITLE]}</p>
                  <p className="text-2xl font-semibold mt-1"><CountUp value={card[VALUE]} /></p>
                </div>
                {iconMap[card[TYPE]]}
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ">
            <h3 className="text-md font-semibold">Category-wise Performance</h3>
            <div className="mt-0">
              <Button onClick={handleMajorCountClick}>View Detail</Button>
            </div>
          </div>

          <div className="space-y-3">
            {categoryPerformance?.map((item: any, index: number) => (
              <div
                key={index}
                className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 rounded-xl bg-primary-100 p-4">
                <div>
                  <p className="font-medium text-gray-800">
                    {item[CATEGORY]}
                  </p>

                  <p className="text-sm text-gray-600">
                    Progress: {item[PROGRESS]}%
                  </p>

                  <p className="text-xs text-gray-500 mt-3">
                    {item[SUBTITLE]}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 lg:justify-end">

                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${item[DELAYED] > 0
                      ? "bg-red-600 text-white border-red-200"
                      : "text-gray-600 border-gray-200"
                      }`}
                  >
                    {item[DELAYED]} Delayed
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${item[SAFETY] > 0
                      ? "bg-red-600 text-white border-red-200"
                      : "text-gray-600 border-gray-200"
                      }`}
                  >
                    {item[SAFETY]} Safety
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full border ${item[QUALITY] > 0
                      ? "bg-red-600 text-white border-red-200"
                      : "text-gray-600 border-gray-200"
                      }`}
                  >
                    {item[QUALITY]} Quality
                  </span>

                </div>
              </div>
            ))}
          </div>
        </Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Upcoming Events:</h3>
            </div>
            {upcomingEvents?.length > 0 ? (
              upcomingEvents.slice(0, 3).map((event: any, idx: number) => (
                <div key={idx} className="flex justify-between items-center mb-1">
                  <p className="text-sm text-gray-600">Your Next event {event.taskName} ({event.eventType}) is on {event.formattedDate}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No upcoming events found</p>
            )}
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <ChartColumn className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Schedule Variance</h3>
            </div>

            <div className="flex justify-between items-center mb-1">
              <p className="text-sm text-gray-600">Current Variance:<span className="font-bold"> {scheduleVariance?.varianceDays ?? 0} Days</span></p>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Auto-calculated from Planned vs Actual dates
            </p>
          </Card>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <HardHat className="w-6 h-6 text-yellow-500" />
                      <h3 className="text-md font-semibold">Safety Audit Compliance</h3>
                    </div>
        
                    <div 
                      onClick={(safetyAuditSummary?.majorNonCompliance ?? 0) > 0 ? handleMajorCountClick : undefined}
                      className={`flex justify-between items-center mb-1 rounded-md transition-colors ${
                        (safetyAuditSummary?.majorNonCompliance ?? 0) > 0 
                          ? "cursor-pointer group hover:bg-gray-50 text-gray-900" 
                          : "cursor-default text-gray-600"
                      }`}
                    >
                      <p className="text-sm">Major Non-Compliance</p>
                      <span 
                        className={`border text-xs px-2 py-1 rounded-full transition-colors ${
                          (safetyAuditSummary?.majorNonCompliance ?? 0) > 0 
                            ? "bg-red-500 text-white group-hover:bg-red-600" 
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {safetyAuditSummary?.majorNonCompliance}
                      </span>
                    </div>
        
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-gray-600">Minor Non-Compliance</p>
                      <span className="border text-xs px-2 py-1 rounded-full">
                        {safetyAuditSummary?.minorNonCompliance}
                      </span>
                    </div>
        
                    <Button
                      disabled
                      className="w-full justify-start bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed rounded-xl h-11">
                      Tasks locked until Compliance = Pass
                    </Button>
                  </Card>
                  <Card className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <Settings className="w-6 h-6 text-yellow-500" />
                      <h3 className="text-md font-semibold">Quality Control</h3>
                    </div>
        
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-sm text-gray-600">Minor Failure</p>
                      <span className="border text-xs px-2 py-1 rounded-full">
                        {qualityAuditSummary?.minorFailure}
                      </span>
                    </div>
        
                    <div 
                      onClick={(qualityAuditSummary?.majorFailure ?? 0) > 0 ? handleMajorCountClick : undefined}
                      className={`flex justify-between items-center mb-1 rounded-md transition-colors ${
                        (qualityAuditSummary?.majorFailure ?? 0) > 0 
                        ? "cursor-pointer group hover:bg-gray-50 text-gray-900" 
                        : "cursor-default text-gray-600"
                      }`}
                    >
                      <p className="text-sm">Major Failure</p>
                      <span 
                        className={`border text-xs px-2 py-1 rounded-full transition-colors ${
                          (qualityAuditSummary?.majorFailure ?? 0) > 0 
                            ? "bg-red-500 text-white group-hover:bg-red-600" 
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {qualityAuditSummary?.majorFailure}
                      </span>
                    </div>
        
        
                    <Button
                      disabled
                      className="w-full justify-start bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed rounded-xl h-11">
                      Task Locked until Acceptable
                    </Button>
                  </Card>
                </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Site Manager Daily Monitoring</h3>
            </div>
 
            {/* <div className="flex justify-between items-center mb-1"> */}
            <p className="text-sm text-gray-600">Last Update: {siteManagerLastUpdate}</p>
            {/* </div> */}
          </Card>
 
          <Card className="p-6 border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <FileCheck className="w-6 h-6 text-yellow-500" />
              <h3 className="text-md font-semibold">Closure Readiness</h3>
            </div>
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600">All Tasks 100% Complete:</p>
                {closureReadiness?.allTasksComplete ? (
                  <CircleCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>
 
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600">All Safety Audits Closed:</p>
                {closureReadiness?.allSafetyAuditsClosed ? (
                  <CircleCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>
 
              <div className="flex items-center gap-3">
                <p className="text-sm text-gray-600">All Quality Audits Closed:</p>
                {closureReadiness?.allQualityAuditsClosed ? (
                  <CircleCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <X className="w-5 h-5 text-red-600" />
                )}
              </div>
            </div>
 
            <Button
              disabled={!closureReadiness?.canClose}
              className={`w-full justify-start rounded-xl h-11 ${
                closureReadiness?.canClose 
                  ? "bg-primary-600 text-white hover:bg-primary-700" 
                  : "bg-gray-200 text-gray-800 hover:bg-gray-100 cursor-not-allowed"
              }`}>
              {closureReadiness?.canClose ? "Close Project" : "Close Project (Disabled Until All Checks Pass)"}
            </Button>
          </Card>
        </div>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Image className="w-5 h-5 text-yellow-500" />
            <h3 className="text-md font-semibold">Client Shared Photos</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {clientSharedPhotos?.map((item: any, index: number) => (
              <div
                key={index}
                className="bg-[#3a3629] rounded-md p-4 min-h-[120px]"
              >
                <p className="text-yellow-400 text-sm font-medium mb-3">
                  {item[CATEGORY]}
                </p>

                <div className="space-y-1 text-xs">
                  <div
                    className="flex justify-between text-white gap-3 rounded transition-colors"     
                  >
                    <span>Total Photos</span>
                    <span className={`text-yellow-400 shrink-0 ${item[TOTAL_PHOTOS] > 0 ? "underline cursor-pointer" : "pointer-events-none"}`} onClick={() => {
                      setSelectedCategory(item[CATEGORY]);
                      setIsPhotoSliderOpen(true);
                    }}>
                      : {String(item[TOTAL_PHOTOS]).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Pending Approval</span>
                    <span className="text-yellow-400 shrink-0">
                      : {String(item[PENDING_APPROVAL]).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Approved</span>
                    <span className="text-yellow-400 shrink-0">
                      : {String(item[APPROVED]).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="flex justify-between text-white gap-3">
                    <span>Rejected</span>
                    <span className="text-yellow-400 shrink-0">
                      : {String(item[REJECTED]).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            disabled
            className="w-full sm:w-fit justify-start bg-gray-200 text-gray-500 hover:bg-gray-200 cursor-not-allowed rounded-xl h-11"
          >
            Tagged Start & Completion images visible to Client Dashboard
          </Button>
        </Card>
      </Card>
      <PhotoSlider
        isOpen={isPhotoSliderOpen}
        onClose={() => setIsPhotoSliderOpen(false)}
        category={selectedCategory}
        photos={selectedPhotos}
        onReview={handleReviewPhoto}
      />
    </div>
  );
};

export default Dashboard;